/* eslint-disable jsx-a11y/media-has-caption */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { Viewer, Worker } from '@react-pdf-viewer/core';
// Import styles
import '@react-pdf-viewer/core/lib/styles/index.css';
// Import pdfjs-dist worker
import * as pdfjs from 'pdfjs-dist';
import { setCurrentTime, pause, seek, play } from '../store/slices/playbackSlice';
import { selectTotalDuration } from '../store/slices/timelineSlice';
import { setQuestionModalVisible } from '../store/slices/editorUISlice';
import { updateAsset, selectAssetById } from '../store/slices/assetsSlice';
import FilledButton from '../../../components/Buttons/FilledButton';
import classes from './PreviewRenderers.module.scss';
import WaveForm from '../../../components/MediaContainer/WaveForm';
import UserService from '../../../services/UserService';
import useCheckMobileScreen from '../../../hooks/useCheckMobileScreen';
import useOnClickOutside from '../../../hooks/useOnClickOutside';
import editIcon from '../../../assets/images/section-menu/edit.svg';

// Set worker source for PDF.js - use CDN with correct version
if (typeof window !== 'undefined') {
  pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
}

/**
 * Maps file extension to MIME type (reverse of backend's getExtensionFromMimeType)
 * @param {string} extension - File extension (with or without dot, e.g., 'pdf' or '.pdf')
 * @returns {string|null} - The MIME type or null if unknown
 */
function getMimeTypeFromExtension(extension) {
  if (!extension) return null;
  
  // Remove leading dot if present
  const ext = extension.toLowerCase().replace(/^\./, '');
  
  // Document MIME types
  const documentExtensionMap = {
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ppt: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  };
  
  if (documentExtensionMap[ext]) {
    return documentExtensionMap[ext];
  }
  
  // Image MIME types
  const imageExtensionMap = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
    bmp: 'image/bmp',
    ico: 'image/x-icon',
  };
  
  if (imageExtensionMap[ext]) {
    return imageExtensionMap[ext];
  }
  
  // Video MIME types
  const videoExtensionMap = {
    mp4: 'video/mp4',
    webm: 'video/webm',
    mov: 'video/quicktime',
    avi: 'video/x-msvideo',
    mkv: 'video/x-matroska',
    flv: 'video/x-flv',
    wmv: 'video/x-ms-wmv',
  };
  
  if (videoExtensionMap[ext]) {
    return videoExtensionMap[ext];
  }
  
  // Audio MIME types
  const audioExtensionMap = {
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    ogg: 'audio/ogg',
    m4a: 'audio/mp4',
    aac: 'audio/aac',
    flac: 'audio/flac',
  };
  
  if (audioExtensionMap[ext]) {
    return audioExtensionMap[ext];
  }
  
  return null;
}

/**
 * Extracts file extension from a URL (before query parameters)
 * @param {string} url - The URL to extract extension from
 * @returns {string|null} - The file extension (without dot) or null
 */
function getExtensionFromUrl(url) {
  if (!url || typeof url !== 'string') return null;
  
  try {
    // Remove query parameters and hash
    const urlWithoutQuery = url.split('?')[0].split('#')[0];
    // Extract extension from the last part of the path
    const match = urlWithoutQuery.match(/\.([a-z0-9]+)$/i);
    return match ? match[1].toLowerCase() : null;
  } catch (error) {
    return null;
  }
}

/**
 * Gets MIME type from asset, using mime_type if available, otherwise inferring from URL extension
 * @param {object} asset - The asset object
 * @returns {string|null} - The MIME type or null
 */
function getAssetMimeType(asset) {
  if (!asset) return null;
  
  // First, try to use the mime_type if it's already set
  if (asset.mime_type) {
    return asset.mime_type;
  }
  
  // If not available, try to infer from URL extension
  const extension = getExtensionFromUrl(asset.url);
  if (extension) {
    return getMimeTypeFromExtension(extension);
  }
  
  return null;
}

export function VideoRenderer({ asset, currentItem }) {
  const dispatch = useDispatch();
  const isPlaying = useSelector((state) => state.playback.isPlaying);
  const currentTime = useSelector((state) => state.playback.currentTime);
  const totalDuration = useSelector(selectTotalDuration);
  const videoRef = useRef();
  const lastDispatchRef = useRef(0);
  const hasAdvancedRef = useRef(false);

  // Get trim settings
  const trimStart = currentItem.settings?.trim_start ?? 0;
  const trimEnd = currentItem.settings?.trim_end ?? 0;

  // Reset advance flag when item changes
  useEffect(() => {
    hasAdvancedRef.current = false;
  }, [currentItem.id]);

  // Handle video playback naturally - let browser handle it
  // Note: Avoid depending on currentTime to prevent re-subscribing each tick
  useEffect(() => {
    if (!videoRef.current) return undefined;

    const video = videoRef.current;
    const videoTimeOffset = currentTime - currentItem.start_time;

    const handleTimeUpdate = () => {
      // Throttle Redux updates to ~10Hz
      const now = performance.now();
      if (now - lastDispatchRef.current < 100) return;
      lastDispatchRef.current = now;

      // Calculate original duration and end boundary
      const originalDuration = asset.duration ?? (currentItem.duration + trimStart + trimEnd);
      const trimmedEndTime = originalDuration - trimEnd;
      
      // HARD BOUNDARY ENFORCEMENT: Don't allow playback outside trim range
      // Ensure we're not playing before the trim start
      if (video.currentTime < trimStart) {
        video.currentTime = trimStart;
        return;
      }
      
      // Ensure we're not playing past the trim end
      if (video.currentTime > trimmedEndTime) {
        video.currentTime = trimmedEndTime;
        video.pause();
        
        const nextTime = currentItem.start_time + currentItem.duration;
        if (nextTime >= totalDuration) {
          dispatch(pause());
          dispatch(setCurrentTime(totalDuration));
        } else {
          dispatch(setCurrentTime(nextTime));
        }
        return;
      }
      
      // Reset the advance flag if we've seeked away from the end
      // This allows replaying the same clip after reaching the end
      if (video.currentTime < trimmedEndTime - 1.0 && hasAdvancedRef.current) {
        hasAdvancedRef.current = false;
      }

      // Update Redux with actual video time + offset
      // Subtract trim_start to get timeline position from video position
      const newTime = currentItem.start_time + (video.currentTime - trimStart);
      dispatch(setCurrentTime(newTime));

      // Check if we've reached the trimmed end (with small tolerance)
      if (video.currentTime >= trimmedEndTime - 0.05 && !hasAdvancedRef.current) {
        hasAdvancedRef.current = true; // Prevent duplicate advances
        const nextTime = currentItem.start_time + currentItem.duration;
        if (nextTime >= totalDuration) {
          // End of timeline - pause and clamp to total duration
          video.pause();
          dispatch(pause());
          dispatch(setCurrentTime(totalDuration));
        } else {
          dispatch(setCurrentTime(nextTime));
        }
      }
    };

    const handleEnded = () => {
      if (hasAdvancedRef.current) return; // Already advanced
      hasAdvancedRef.current = true;
      
      const newTime = currentItem.start_time + currentItem.duration;
      if (newTime >= totalDuration) {
        // End of timeline - pause video element immediately
        video.pause();
        dispatch(pause());
        dispatch(setCurrentTime(totalDuration));
      } else {
        dispatch(setCurrentTime(newTime));
      }
    };

    if (isPlaying) {
      // Set video currentTime to trimmed start + offset
      video.currentTime = Math.max(0, trimStart + videoTimeOffset);
      // Try to play - if autoplay is blocked, log for debugging
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          // Autoplay was prevented - this is expected in some browsers
          // The user will need to click play manually
          console.error('Autoplay prevented:', error);
        });
      }
      video.addEventListener('timeupdate', handleTimeUpdate);
      video.addEventListener('ended', handleEnded);
    } else {
      video.pause();
    }

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
    };
  }, [isPlaying, currentItem, dispatch, totalDuration, trimStart, trimEnd, asset.duration]);

  // Sync video element to external seeks without re-subscribing
  useEffect(() => {
    if (!videoRef.current) return undefined;
    // Desired video time = trim_start + timeline offset
    const timelineOffset = currentTime - currentItem.start_time;
    const desired = Math.max(trimStart, trimStart + timelineOffset);
    
    // Calculate original duration to ensure we don't seek past the trimmed end
    const originalDuration = asset.duration ?? (currentItem.duration + trimStart + trimEnd);
    const maxTime = originalDuration - trimEnd;
    const clampedDesired = Math.min(desired, maxTime);
    
    const diff = Math.abs(videoRef.current.currentTime - clampedDesired);
    if (diff > 0.15) {
      videoRef.current.currentTime = clampedDesired;
    }
    return undefined;
  }, [currentTime, currentItem, trimStart, trimEnd, asset.duration]);

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <video
        ref={videoRef}
        src={asset.url}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
        }}
        muted={false}
        playsInline
      />
    </div>
  );
}

export function AudioRenderer({ asset, currentItem }) {
  const dispatch = useDispatch();
  const isPlaying = useSelector((state) => state.playback.isPlaying);
  const currentTime = useSelector((state) => state.playback.currentTime);
  const totalDuration = useSelector(selectTotalDuration);
  const audioRef = useRef();
  const lastDispatchRef = useRef(0);
  const containerRef = useRef();

  const trimStart = currentItem.settings?.trim_start ?? 0;
  const trimEnd = currentItem.settings?.trim_end ?? 0;

  useEffect(() => {
    if (!audioRef.current) return undefined;
    const audio = audioRef.current;
    const audioTimeOffset = currentTime - currentItem.start_time;

    const handleTimeUpdate = () => {
      const now = performance.now();
      if (now - lastDispatchRef.current < 100) return;
      lastDispatchRef.current = now;

      const originalDuration = asset.duration ?? (currentItem.duration + trimStart + trimEnd);
      const trimmedEndTime = originalDuration - trimEnd;

      if (audio.currentTime < trimStart) {
        audio.currentTime = trimStart;
        return;
      }
      if (audio.currentTime > trimmedEndTime) {
        audio.currentTime = trimmedEndTime;
        audio.pause();
        const nextTime = currentItem.start_time + currentItem.duration;
        if (nextTime >= totalDuration) {
          dispatch(pause());
          dispatch(setCurrentTime(totalDuration));
        } else {
          dispatch(setCurrentTime(nextTime));
        }
        return;
      }

      const newTime = currentItem.start_time + (audio.currentTime - trimStart);
      dispatch(setCurrentTime(newTime));
    };

    const handleEnded = () => {
      const newTime = currentItem.start_time + currentItem.duration;
      if (newTime >= totalDuration) {
        audio.pause();
        dispatch(pause());
        dispatch(setCurrentTime(totalDuration));
      } else {
        dispatch(setCurrentTime(newTime));
      }
    };

    if (isPlaying) {
      audio.currentTime = Math.max(0, trimStart + audioTimeOffset);
      audio.play().catch(() => {});
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('ended', handleEnded);
    } else {
      audio.pause();
    }

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [isPlaying, currentItem, dispatch, totalDuration, trimStart, trimEnd, asset.duration]);

  useEffect(() => {
    if (!audioRef.current) return undefined;
    const timelineOffset = currentTime - currentItem.start_time;
    const desired = Math.max(trimStart, trimStart + timelineOffset);
    const originalDuration = asset.duration ?? (currentItem.duration + trimStart + trimEnd);
    const maxTime = originalDuration - trimEnd;
    const clampedDesired = Math.min(desired, maxTime);
    if (Math.abs(audioRef.current.currentTime - clampedDesired) > 0.15) {
      audioRef.current.currentTime = clampedDesired;
    }
    return undefined;
  }, [currentTime, currentItem, trimStart, trimEnd, asset.duration]);

  const itemDuration = currentItem.duration || asset.duration || 1;
  const itemStartTime = currentItem.start_time || 0;
  const relativeTime = Math.max(0, currentTime - itemStartTime);
  const seekPosition = itemDuration > 0 ? Math.min(1, relativeTime / itemDuration) : 0;

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }} ref={containerRef}>
      <WaveForm
        containerRef={containerRef}
        source={asset.url}
        isPlaying={isPlaying}
        isPaused={!isPlaying && currentTime > 0}
        isEnded={currentTime >= itemStartTime + itemDuration}
        seek={seekPosition}
        fullHeight
      />
      {/* Hidden audio element to drive Redux time updates */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audioRef} src={asset.url} style={{ display: 'none' }} />
    </div>
  );
}

export function ImageRenderer({ asset }) {
  const dispatch = useDispatch();
  const isPlaying = useSelector((state) => state.playback.isPlaying);
  const currentTime = useSelector((state) => state.playback.currentTime);
  const totalDuration = useSelector(selectTotalDuration);
  const isMobile = useCheckMobileScreen();

  // Get latest asset from Redux to ensure we have current state (reacts to updates)
  const currentAsset = useSelector((state) => selectAssetById(state, asset.id)) || asset;

  // Editing state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editingTitle, setEditingTitle] = useState('');
  const [editingDescription, setEditingDescription] = useState('');
  const [hoveredField, setHoveredField] = useState(null);

  const titleInputRef = useRef(null);
  const descriptionInputRef = useRef(null);
  const titleContainerRef = useRef(null);
  const descriptionContainerRef = useRef(null);

  // Handle timer-based playback for images - plays through all items
  useEffect(() => {
    if (!isPlaying || totalDuration === 0) return undefined;

    const interval = setInterval(() => {
      const newTime = Math.min(currentTime + 0.1, totalDuration);
      dispatch(setCurrentTime(newTime));

      // Stop at end
      if (newTime >= totalDuration) {
        dispatch(pause());
      }
    }, 100);

    return () => clearInterval(interval);
  }, [isPlaying, currentTime, totalDuration, dispatch]);

  // Get title and description from current asset metadata (reacts to Redux updates)
  const title = currentAsset.metadata?.attachmentName || currentAsset.name || '';
  const description = currentAsset.metadata?.attachmentDescription || '';

  // Initialize editing values when entering edit mode
  useEffect(() => {
    if (isEditingTitle) {
      setEditingTitle(title);
    }
  }, [isEditingTitle, title]);

  useEffect(() => {
    if (isEditingDescription) {
      setEditingDescription(description);
    }
  }, [isEditingDescription, description]);

  // Focus inputs when editing starts
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      setTimeout(() => {
        titleInputRef.current?.focus();
        titleInputRef.current?.select();
      }, 0);
    }
  }, [isEditingTitle]);

  useEffect(() => {
    if (isEditingDescription && descriptionInputRef.current) {
      setTimeout(() => {
        descriptionInputRef.current?.focus();
        descriptionInputRef.current?.select();
      }, 0);
    }
  }, [isEditingDescription]);

  // Handle save title
  const handleSaveTitle = useCallback(() => {
    if (!isEditingTitle) return;
    
    const newTitle = editingTitle.trim();
    const currentTitle = currentAsset.metadata?.attachmentName || currentAsset.name || '';
    
    if (newTitle !== currentTitle) {
      // Preserve all existing metadata
      const existingMetadata = currentAsset.metadata || {};
      
      // Update Redux state only - backend sync happens in saveDraft/publish
      dispatch({
        ...updateAsset({
          id: currentAsset.id,
          changes: {
            metadata: {
              ...existingMetadata,
              attachmentName: newTitle || currentAsset.name,
            },
          },
        }),
        meta: { undoable: true },
      });
    }
    setIsEditingTitle(false);
  }, [isEditingTitle, editingTitle, currentAsset, dispatch]);

  // Handle save description
  const handleSaveDescription = useCallback(() => {
    if (!isEditingDescription) return;
    
    const newDescription = editingDescription.trim();
    const currentDescription = currentAsset.metadata?.attachmentDescription || '';
    
    if (newDescription !== currentDescription) {
      // Preserve all existing metadata
      const existingMetadata = currentAsset.metadata || {};
      
      // Update Redux state only - backend sync happens in saveDraft/publish
      dispatch({
        ...updateAsset({
          id: currentAsset.id,
          changes: {
            metadata: {
              ...existingMetadata,
              attachmentDescription: newDescription,
            },
          },
        }),
        meta: { undoable: true },
      });
    }
    setIsEditingDescription(false);
  }, [isEditingDescription, editingDescription, currentAsset, dispatch]);

  // Handle cancel editing
  const handleCancelTitle = () => {
    setEditingTitle(title);
    setIsEditingTitle(false);
  };

  const handleCancelDescription = () => {
    setEditingDescription(description);
    setIsEditingDescription(false);
  };

  // Handle key presses
  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveTitle();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelTitle();
    }
  };

  const handleDescriptionKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveDescription();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancelDescription();
    }
  };

  // Use click outside to save
  useOnClickOutside(titleContainerRef, handleSaveTitle, [descriptionContainerRef]);
  useOnClickOutside(descriptionContainerRef, handleSaveDescription, [titleContainerRef]);

  // Account for seekbar height - same values used elsewhere in the codebase
  const seekbarHeight = isMobile ? 114 : 84;

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <img
        src={asset.url}
        alt={asset.name}
        style={{
          maxWidth: '100%',
          maxHeight: '100%',
          width: 'auto',
          height: 'auto',
          objectFit: 'contain',
        }}
      />
      {(title || description || isEditingTitle || isEditingDescription) && (
        <div
          style={{
            position: 'absolute',
            bottom: seekbarHeight, // Position above seekbar
            left: 0,
            right: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            color: '#ffffff',
            padding: '16px 20px',
            zIndex: 10,
          }}
        >
          {/* Title */}
          <div
            ref={titleContainerRef}
            onMouseEnter={() => !isEditingTitle && setHoveredField('title')}
            onMouseLeave={() => setHoveredField(null)}
            style={{
              marginBottom: (description || isEditingDescription) ? '8px' : 0,
            }}
          >
            {isEditingTitle ? (
              <input
                ref={titleInputRef}
                value={editingTitle}
                onChange={(e) => setEditingTitle(e.target.value)}
                onKeyDown={handleTitleKeyDown}
                onClick={(e) => e.stopPropagation()}
                style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  lineHeight: '1.4',
                  color: '#ffffff',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: '2px dashed #ffffff',
                  outline: 'none',
                  width: '100%',
                  padding: 0,
                  margin: 0,
                }}
              />
            ) : (
              <div
                onClick={() => setIsEditingTitle(true)}
                style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  lineHeight: '1.4',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  textDecoration: hoveredField === 'title' ? 'underline' : 'none',
                }}
              >
                {title}
                {hoveredField === 'title' && (
                  <img
                    src={editIcon}
                    alt="Edit"
                    style={{
                      width: '16px',
                      height: '16px',
                    }}
                  />
                )}
              </div>
            )}
          </div>

          {/* Description */}
          {(description || isEditingDescription) && (
            <div
              ref={descriptionContainerRef}
              onMouseEnter={() => !isEditingDescription && setHoveredField('description')}
              onMouseLeave={() => setHoveredField(null)}
            >
              {isEditingDescription ? (
                <input
                  ref={descriptionInputRef}
                  value={editingDescription}
                  onChange={(e) => setEditingDescription(e.target.value)}
                  onKeyDown={handleDescriptionKeyDown}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '1.5',
                    opacity: 0.9,
                    color: '#ffffff',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: '2px dashed #ffffff',
                    outline: 'none',
                    width: '100%',
                    padding: 0,
                    margin: 0,
                  }}
                />
              ) : (
                <div
                  onClick={() => setIsEditingDescription(true)}
                  style={{
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '1.5',
                    opacity: 0.9,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    textDecoration: hoveredField === 'description' ? 'underline' : 'none',
                  }}
                >
                  {description}
                  {hoveredField === 'description' && (
                    <img
                      src={editIcon}
                      alt="Edit"
                      style={{
                        width: '14px',
                        height: '14px',
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function DocumentRenderer({ asset, currentItem }) {
  // Early return guard - but AFTER all hooks to maintain hook order
  // We'll handle this differently by ensuring all hooks run first
  
  const dispatch = useDispatch();
  const isPlaying = useSelector((state) => state.playback.isPlaying);
  const currentTime = useSelector((state) => state.playback.currentTime);
  const totalDuration = useSelector(selectTotalDuration);
  const timelineItems = useSelector((state) => state.timeline.items);
  const isPreviewContext = useSelector(
    (state) => state.editorUI.isPreviewMode || state.editorUI.isPreviewModalVisible,
  );
  const isMobile = useCheckMobileScreen();
  const [isPdfEnlarged, setIsPdfEnlarged] = useState(false);

  // Safety check for asset and currentItem - use defaults to prevent errors
  const safeAsset = asset || {};
  const safeCurrentItem = currentItem || { id: '', start_time: 0, duration: 0 };

  // Find the next item in the timeline
  const currentIndex = timelineItems.findIndex((item) => item.id === safeCurrentItem.id);
  const nextItem =
    currentIndex >= 0 && currentIndex < timelineItems.length - 1
      ? timelineItems[currentIndex + 1]
      : null;
  const isLastItem = !nextItem;

  // Handle timer-based playback for documents - pause at end of this item
  useEffect(() => {
    if (!isPlaying || !safeCurrentItem.start_time) return undefined;

    const itemEnd = safeCurrentItem.start_time + safeCurrentItem.duration;
    // Stop slightly before itemEnd to avoid hitting the next item boundary
    const stopTime = itemEnd - 0.02;

    const interval = setInterval(() => {
      const nextTime = Math.min(currentTime + 0.1, stopTime);
      dispatch(setCurrentTime(nextTime));

      // Pause at the end of this document segment and let user continue
      if (nextTime >= stopTime) {
        dispatch(pause());
      }
    }, 100);

    return () => clearInterval(interval);
  }, [isPlaying, currentTime, safeCurrentItem, dispatch]);

  // Handle continue button click
  const handleContinue = () => {
    if (nextItem) {
      // Seek to the next item's start time and play
      dispatch(seek(nextItem.start_time));
      dispatch(play());
    } else {
      // Last item - just pause
      dispatch(pause());
      dispatch(seek(totalDuration));
    }
  };

  // Check if it's a PDF - use MIME type only (not filename)
  // Get MIME type from asset.mime_type or infer from URL extension
  const assetMimeType = getAssetMimeType(safeAsset);
  const isPDF = assetMimeType === 'application/pdf';

  // Check if asset is ready
  const isAssetReady = Boolean(asset && asset.url);

  // Check if we're paused at the end of this document item
  const itemEnd = safeCurrentItem.start_time + safeCurrentItem.duration;
  const stopTime = itemEnd - 0.02;
  const isAtEnd = !isPlaying && Math.abs(currentTime - stopTime) < 0.01;
  const documentShouldShowPreviewButton = isPreviewContext && isLastItem && isAtEnd;
  const documentShouldShowButton = !isLastItem || documentShouldShowPreviewButton;
  const documentContinueButton = documentShouldShowButton ? (
    <div
      className={isAtEnd ? classes.continueButtonContainer : ''}
      style={{
        position: 'absolute',
        bottom: isMobile ? 114 : 84, // 30px higher on mobile to avoid seekbar
        right: 24,
        zIndex: 10,
      }}
    >
      <FilledButton onClick={handleContinue} width={140} style={{ height: 50 }}>
        Continue
      </FilledButton>
    </div>
  ) : null;

  // NO EARLY RETURNS - always return JSX to maintain hook consistency
  // Render loading state if asset is not ready
  if (!isAssetReady) {
    return (
      <div style={{ textAlign: 'center', color: '#e5e7eb' }}>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Loading document...</div>
      </div>
    );
  }

  // Render PDF viewer if it's a PDF
  if (isPDF) {
    return (
      <div 
        className={isPdfEnlarged ? classes.pdfEnlarged : ''}
        style={{ position: 'relative', width: '100%', height: '100%', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
      >
        <Worker workerUrl={pdfjs.GlobalWorkerOptions.workerSrc}>
          <div
            className={classes.pdfViewerContainer}
            style={{
              width: '100%',
              height: '100%',
              borderRadius: 8,
              overflow: isPdfEnlarged ? 'auto' : 'hidden',
              backgroundColor: '#fff',
            }}
          >
            <Viewer 
              fileUrl={safeAsset.url}
              defaultScale={isPdfEnlarged ? 1.0 : undefined}
              key={isPdfEnlarged ? 'enlarged' : 'shrunk'}
            />
          </div>
        </Worker>
        {/* Toggle enlarge/shrink button in top left */}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsPdfEnlarged(!isPdfEnlarged);
          }}
          style={{
            position: 'absolute',
            top: 60,
            left: 24,
            padding: '8px 16px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: '#fff',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: 500,
            border: 'none',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            zIndex: 11,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {isPdfEnlarged ? (
              // Shrink icon (minus/compress)
              <>
                <path
                  d="M4 8H12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M2 2L14 14M14 2L2 14"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </>
            ) : (
              // Enlarge icon (expand)
              <>
                <path
                  d="M8 2V14M2 8H14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </>
            )}
          </svg>
          {isPdfEnlarged ? 'Fit to Screen' : 'Enlarge'}
        </button>
        {/* Download link for attachment - positioned to avoid toolbar overlap */}
        <a
          href={safeAsset.url}
          download
          target="_blank"
          rel="noreferrer"
          style={{
            position: 'absolute',
            top: 60, // Below the PDF viewer toolbar
            right: 24,
            zIndex: 11,
            fontSize: 14,
            padding: '6px 10px',
            background: 'rgba(0,0,0,0.6)',
            color: '#fff',
            borderRadius: 6,
            textDecoration: 'none',
            transition: 'background 0.2s',
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(0,0,0,0.8)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(0,0,0,0.6)';
          }}
        >
          Download
        </a>
        {/* Continue Button - Bottom Right */}
        {documentContinueButton}
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
      }}
    >
      <div
        style={{
          background: 'white',
          padding: 32,
          borderRadius: 8,
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          maxWidth: '100%',
          maxHeight: '100%',
          width: 'auto',
          height: 'auto',
          minWidth: 0,
          minHeight: 0,
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'auto',
          boxSizing: 'border-box',
        }}
      >
          <a
            href={safeAsset.url}
            download
            target="_blank"
            rel="noreferrer"
            title={`Download ${safeAsset.name || 'attachment'}`}
            style={{ display: 'inline-block', textDecoration: 'none', color: 'inherit' }}
          >
            <div style={{ fontSize: 48, marginBottom: 16 }}>📄</div>
          </a>
        <a
          href={safeAsset.url}
          download
          target="_blank"
          rel="noreferrer"
          title={`Download ${safeAsset.name || 'attachment'}`}
          style={{
            fontSize: 20,
            fontWeight: 600,
            color: '#2563eb',
            marginBottom: 8,
            textDecoration: 'none',
          }}
        >
          {safeAsset.name}
        </a>
        <a
          href={safeAsset.url}
          download
          target="_blank"
          rel="noreferrer"
          title={`Download ${safeAsset.name || 'attachment'}`}
          style={{
            fontSize: 14,
            color: '#2563eb',
            textDecoration: 'none',
            cursor: 'pointer',
          }}
        >
          Click to download and view file
        </a>
      </div>
      {/* Continue Button - Bottom Right */}
      {documentContinueButton}
    </div>
  );
}

export function TextRenderer({ asset, currentItem }) {
  const dispatch = useDispatch();
  const isPlaying = useSelector((state) => state.playback.isPlaying);
  const currentTime = useSelector((state) => state.playback.currentTime);
  const totalDuration = useSelector(selectTotalDuration);
  const timelineItems = useSelector((state) => state.timeline.items);
  const isPreviewContext = useSelector(
    (state) => state.editorUI.isPreviewMode || state.editorUI.isPreviewModalVisible,
  );
  const isMobile = useCheckMobileScreen();

  const currentIndex = timelineItems.findIndex((item) => item.id === currentItem.id);
  const nextItem = currentIndex >= 0 && currentIndex < timelineItems.length - 1
    ? timelineItems[currentIndex + 1]
    : null;
  const isLastItem = !nextItem;

  useEffect(() => {
    if (!isPlaying) return undefined;
    const itemEnd = currentItem.start_time + currentItem.duration;
    const stopTime = itemEnd - 0.02;
    const interval = setInterval(() => {
      const nextTime = Math.min(currentTime + 0.1, stopTime);
      dispatch(setCurrentTime(nextTime));
      if (nextTime >= stopTime) {
        dispatch(pause());
      }
    }, 100);
    return () => clearInterval(interval);
  }, [isPlaying, currentTime, currentItem, dispatch]);

  const handleContinue = () => {
    if (nextItem) {
      dispatch(seek(nextItem.start_time));
      dispatch(play());
    } else {
      dispatch(pause());
      dispatch(seek(totalDuration));
    }
  };

  const itemEnd = currentItem.start_time + currentItem.duration;
  const stopTime = itemEnd - 0.02;
  const isAtEnd = !isPlaying && Math.abs(currentTime - stopTime) < 0.01;
  const shouldShowPreviewButton = isPreviewContext && isLastItem && isAtEnd;
  const shouldShowButton = !isLastItem || shouldShowPreviewButton;
  const continueButton = shouldShowButton ? (
    <div
      className={isAtEnd ? (classes?.continueButtonContainer || '') : ''}
      style={{ position: 'absolute', bottom: isMobile ? 114 : 84, right: 24, zIndex: 10 }}
    >
      <FilledButton onClick={handleContinue} width={140} style={{ height: 50 }}>
        Continue
      </FilledButton>
    </div>
  ) : null;

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
      }}
    >
      <div
        style={{
          background: 'white',
          padding: 32,
          borderRadius: 8,
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          maxWidth: 720,
          width: '92%',
          textAlign: 'left',
          color: '#111827',
          lineHeight: 1.5,
          maxHeight: '80%',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>{asset.name || 'Text'}</div>
        <div
          style={{
            fontSize: 16,
            lineHeight: 1.6,
            wordBreak: 'break-word',
          }}
          dangerouslySetInnerHTML={{ __html: asset.text || '' }}
        />
      </div>
      {continueButton}
    </div>
  );
}

export function AssignedSectionRenderer({ asset, currentItem, user, fetchedSections, onAddContent }) {
  const dispatch = useDispatch();
  const isPlaying = useSelector((state) => state.playback.isPlaying);
  const currentTime = useSelector((state) => state.playback.currentTime);
  const totalDuration = useSelector(selectTotalDuration);
  const timelineItems = useSelector((state) => state.timeline.items);

  // Find the next item in the timeline
  const currentIndex = timelineItems.findIndex((item) => item.id === currentItem.id);
  const nextItem = currentIndex >= 0 && currentIndex < timelineItems.length - 1
    ? timelineItems[currentIndex + 1]
    : null;
  const isLastItem = !nextItem;

  // Timer-based playback like DocumentRenderer: advance through item, pause near end
  useEffect(() => {
    if (!isPlaying) return undefined;

    const itemEnd = currentItem.start_time + currentItem.duration;
    const stopTime = itemEnd - 0.02;

    const interval = setInterval(() => {
      const nextTime = Math.min(currentTime + 0.1, stopTime);
      dispatch(setCurrentTime(nextTime));

      if (nextTime >= stopTime) {
        dispatch(pause());
      }
    }, 100);

    return () => clearInterval(interval);
  }, [isPlaying, currentTime, currentItem, dispatch]);

  const handleContinue = () => {
    if (nextItem) {
      dispatch(seek(nextItem.start_time));
      dispatch(play());
    } else {
      dispatch(pause());
      dispatch(seek(totalDuration));
    }
  };

  const itemEnd = currentItem.start_time + currentItem.duration;
  const stopTime = itemEnd - 0.02;
  const isAtEnd = !isPlaying && Math.abs(currentTime - stopTime) < 0.01;

  const assignedEmail = asset.metadata?.assignedEmail || asset.name?.replace('Assigned to ', '') || 'User';
  const assignedUserId = asset.metadata?.assignedUserId;
  
  // Check if current user is assigned to this section
  const isAssignedToCurrentUser = user?.id && assignedUserId === user.id;
  
  // Find the original section to get assignment message
  const originalSection = fetchedSections?.find(
    (s) => s.id === asset.metadata?.sectionId
  );
  const assignmentMessage = originalSection?.text || asset.metadata?.message || '';

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        color: '#F3F4F6',
      }}
    >
      <div
        style={{
          textAlign: 'center',
          padding: '40px',
          maxWidth: '600px',
        }}
      >
        <div
          style={{
            fontSize: 48,
            marginBottom: 24,
          }}
        >
          👤
        </div>
        {isAssignedToCurrentUser ? (
          <>
            <div
              style={{
                fontSize: 24,
                fontWeight: 600,
                marginBottom: 12,
              }}
            >
              This Section is Assigned to You
            </div>
            {assignmentMessage && (
              <div
                style={{
                  fontSize: 16,
                  color: '#9CA3AF',
                  marginBottom: 24,
                  padding: '16px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                }}
              >
                {assignmentMessage}
              </div>
            )}
            <div
              style={{
                fontSize: 16,
                color: '#9CA3AF',
                marginBottom: 32,
              }}
            >
              Add your content to complete this section
            </div>
            {onAddContent && (
              <FilledButton
                onClick={() => {
                  dispatch(pause());
                  onAddContent();
                }}
                width={200}
                style={{ marginBottom: 16 }}
              >
                Add Content
              </FilledButton>
            )}
          </>
        ) : (
          <>
            <div
              style={{
                fontSize: 24,
                fontWeight: 600,
                marginBottom: 12,
              }}
            >
              Section Assigned
            </div>
            <div
              style={{
                fontSize: 16,
                color: '#9CA3AF',
                marginBottom: 32,
              }}
            >
              This section is assigned to <strong>{assignedEmail}</strong>. Waiting for contribution...
            </div>
          </>
        )}
        {isAtEnd && !isLastItem && (
          <FilledButton
            onClick={handleContinue}
            width={200}
          >
            Continue
          </FilledButton>
        )}
      </div>
    </div>
  );
}

export function QuestionRenderer({ asset, currentItem }) {
  const dispatch = useDispatch();
  const isPlaying = useSelector((state) => state.playback.isPlaying);
  const currentTime = useSelector((state) => state.playback.currentTime);
  const totalDuration = useSelector(selectTotalDuration);
  const timelineItems = useSelector((state) => state.timeline.items);
  const isPreviewContext = useSelector(
    (state) => state.editorUI.isPreviewMode || state.editorUI.isPreviewModalVisible,
  );
  const isMobile = useCheckMobileScreen();
  
  // Get current user to check if they've answered
  const { data: user } = useQuery('me', UserService.getMe);
  
  // Find user's answer if available
  const questionAnswers = asset.metadata?.questionAnswers || [];
  const userAnswer = user?.id
    ? questionAnswers.find((answr) => answr.User?.id === user.id)
    : null;
  const hasAnswered = !!userAnswer;

  // Find the next item in the timeline
  const currentIndex = timelineItems.findIndex((item) => item.id === currentItem.id);
  const nextItem = currentIndex >= 0 && currentIndex < timelineItems.length - 1
    ? timelineItems[currentIndex + 1]
    : null;
  const isLastItem = !nextItem;

  // Timer-based playback like DocumentRenderer: advance through item, pause near end
  useEffect(() => {
    if (!isPlaying) return undefined;

    const itemEnd = currentItem.start_time + currentItem.duration;
    const stopTime = itemEnd - 0.02;

    const interval = setInterval(() => {
      const nextTime = Math.min(currentTime + 0.1, stopTime);
      dispatch(setCurrentTime(nextTime));

      if (nextTime >= stopTime) {
        dispatch(pause());
      }
    }, 100);

    return () => clearInterval(interval);
  }, [isPlaying, currentTime, currentItem, dispatch]);

  const handleContinue = () => {
    if (nextItem) {
      dispatch(seek(nextItem.start_time));
      dispatch(play());
    } else {
      dispatch(pause());
      dispatch(seek(totalDuration));
    }
  };

  const itemEnd = currentItem.start_time + currentItem.duration;
  const stopTime = itemEnd - 0.02;
  const isAtEnd = !isPlaying && Math.abs(currentTime - stopTime) < 0.01;
  const shouldShowPreviewButton = isPreviewContext && isLastItem && isAtEnd;
  const shouldShowButton = !isLastItem || shouldShowPreviewButton;
  const continueButton = shouldShowButton ? (
    <div
      className={isAtEnd ? (classes?.continueButtonContainer || '') : ''}
      style={{ position: 'absolute', bottom: isMobile ? 114 : 84, right: 24, zIndex: 10 }}
    >
      <FilledButton onClick={handleContinue} width={140} style={{ height: 50 }}>
        Continue
      </FilledButton>
    </div>
  ) : null;

  // Consistent aesthetic with DocumentRenderer
  const questionText = asset.text || asset.name || 'Question';
  const answerText = userAnswer?.text || '';

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
      }}
    >
      <div
        style={{
          background: 'white',
          padding: 32,
          borderRadius: 8,
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          maxWidth: 720,
          width: '92%',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 42, marginBottom: 12 }}>❓</div>
        <p
          style={{
            fontSize: 20,
            fontWeight: 600,
            color: '#1f2937',
            marginBottom: 16,
          }}
        >
          {questionText}
        </p>

        {hasAnswered ? (
          // User has answered - show their answer with edit button
          <>
            <div
              style={{
                marginTop: 16,
                marginBottom: 16,
                padding: 16,
                background: '#F3F4F6',
                borderRadius: 8,
                textAlign: 'left',
                position: 'relative',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#6B7280' }}>
                  Your Answer:
                </div>
                <button
                  onClick={() => {
                    // Pause playback and open the modal with the existing answer
                    dispatch(pause());
                    dispatch(setQuestionModalVisible(true));
                  }}
                  type="button"
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#2563eb',
                    fontSize: 16,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    padding: 0,
                    fontFamily: 'inherit',
                    fontWeight: 500,
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#1d4ed8';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#2563eb';
                  }}
                >
                  Edit
                </button>
              </div>
              <div style={{ fontSize: 16, color: '#111827', whiteSpace: 'pre-wrap' }}>
                {answerText}
              </div>
            </div>
            <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 8 }}>
              ✓ Response submitted
            </div>
          </>
        ) : (
          // User hasn't answered - show question options (read-only preview)
          <>
        {asset.options?.type === 'Single Choice' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {asset.options.options?.map((option) => (
              <button
                key={option}
                type="button"
                style={{
                  padding: '10px 16px',
                  borderRadius: 8,
                  border: '1px solid #E5E7EB',
                  background: '#F9FAFB',
                  color: '#111827',
                  cursor: 'default',
                }}
              >
                {option}
              </button>
            ))}
          </div>
        )}

        {asset.options?.type === 'Multiple Choice' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
            {asset.options.options?.map((option, index) => {
              const inputId = `question-${asset.id || 'q'}-opt-${index}`;
              return (
                <label
                  key={option}
                  htmlFor={inputId}
                  style={{ display: 'flex', gap: 10, alignItems: 'center', color: '#111827' }}
                >
                  <input id={inputId} type="checkbox" disabled />
                  <span>{option}</span>
                </label>
              );
            })}
          </div>
        )}

        {(asset.options?.type === 'Single Text Field' || !asset.options) && (
          <textarea
            placeholder="Type your answer... (preview)"
            disabled
            style={{
              width: '100%',
              height: 120,
              padding: 12,
              borderRadius: 8,
              border: '1px solid #E5E7EB',
              background: '#F9FAFB',
              color: '#6B7280',
              resize: 'none',
            }}
          />
        )}

        {asset.isAnswerRequired && (
          <div style={{ marginTop: 12, fontSize: 12, color: '#9CA3AF' }}>
            Answer required (preview)
          </div>
            )}
          </>
        )}
      </div>

      {/* Continue Button - bottom right, like DocumentRenderer */}
      {continueButton}
    </div>
  );
}

